<template>
  <div>
    <v-row no-gutters>
      <span class="textLight--text font-weight-medium text-h5 text-left">
        {{ $t("user.profile.share") }}
      </span>
    </v-row>

    <v-row>
      <v-col>
        <v-card flat rounded class="py-6 py-xl-8 px-6">
          <v-row justify="center">
            <v-col class="d-flex justify-center">
              <div
                ref="qrCodeSvg"
                class="white pa-3 pb-2 rounded-1"
                v-html="
                  qrCodeSvg($vuetify.breakpoint.lgAndUp ? 260 : 180, true)
                "
                :style="{
                  width: $vuetify.breakpoint.lgAndUp ? '260px' : '180px',
                }"
              ></div>
            </v-col>
          </v-row>

          <v-row class="mt-2">
            <v-col>
              <v-btn
                outlined
                block
                class="subtitle-2 textLight--text text-capitalize rounded-2"
                depressed
                :loading="downloadQrCodeLoading"
                @click="downloadQrCode"
              >
                <v-icon left small> mdi-download-outline </v-icon>
                {{ $t("user.profile.download") }}
              </v-btn>
            </v-col>

            <v-col>
              <v-speed-dial v-model="shareFab">
                <template v-slot:activator>
                  <v-btn
                    v-model="shareFab"
                    outlined
                    block
                    class="subtitle-2 textLight--text text-capitalize rounded-2"
                    depressed
                  >
                    <v-icon :left="!shareFab" small>
                      {{ shareFab ? "mdi-close" : "mdi-share-outline" }}
                    </v-icon>

                    <span v-if="!shareFab">{{ $t("user.profile.share") }}</span>
                  </v-btn>
                </template>

                <v-card class="pa-2 text-center">
                  <share-it
                    icons
                    outline
                    round
                    :text="$t('user.profile.checkoutMyProfile')"
                    :url="shareUrl"
                    :targets="shareTargets"
                  />
                </v-card>
              </v-speed-dial>
            </v-col>
          </v-row>

          <v-row class="mt-6 px-3">
            <span
              class="
                subtitle-1
                font-weight-light
                textLight--text
                text-start text-ellipse-1
              "
              style="max-width: 90%"
            >
              {{ shareUrl }}
            </span>

            <v-spacer />

            <v-progress-circular
              v-if="copyingShareUrl"
              indeterminate
              size="20"
            />

            <v-icon v-else spin small @click="copyToClipBoard(shareUrl)">
              mdi-content-copy
            </v-icon>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col class="pt-0">
        <v-btn
          v-if="userProfile"
          block
          class="subtitle-1 textLight--text text-capitalize rounded-2"
          depressed
          color="button"
          large
          :loading="downloadProfileLoading"
          @click="downloadProfileCardAsPdf"
        >
          <v-icon left small> mdi-download-outline </v-icon>

          {{ $t("user.profile.downloadMyCard") }}
        </v-btn>

        <v-btn
          v-if="userProfile"
          block
          class="subtitle-1 textLight--text text-capitalize rounded-2 mt-3"
          depressed
          color="button"
          large
          :loading="downloadCertificatesLoading"
          @click="downloadCertificatesAsPdf"
        >
          <v-icon left small> mdi-download-outline </v-icon>

          {{ $t("user.profile.downloadMyCertificates") }}
        </v-btn>

        <v-btn
          v-else
          block
          class="subtitle-1 text-capitalize rounded-2"
          :class="loading ? 'primary--text' : 'error--text'"
          depressed
          color="button"
          large
          :loading="loading"
          @click="refetchGetProfile"
        >
          <v-icon left small> mdi-replay </v-icon>

          {{ $t("retry") }}
        </v-btn>
      </v-col>
    </v-row>

    <v-dialog
      v-model="downloadProfileDialog"
      :width="previewProfileCardHeight * 3"
      :fullscreen="$vuetify.breakpoint.xsOnly"
    >
      <v-card flat color="grey" class="mouse-cursor">
        <v-toolbar>
          <v-btn icon @click="downloadProfileDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>

          <v-toolbar-title>
            {{ $t("user.profile.downloadMyCard") }}
          </v-toolbar-title>

          <v-spacer />

          <v-toolbar-items v-if="downloadProfileHtml">
            <v-btn
              icon
              :loading="downloadProfileLoading"
              @click="downloadProfileCardAsPdf"
            >
              <v-icon> mdi-file-pdf-box </v-icon>
            </v-btn>

            <!--            <v-btn
              icon
              :loading="downloadProfileLoading"
              @click="downloadProfileCardAsImage"
            >
              <v-icon> mdi-image </v-icon>
            </v-btn>-->
          </v-toolbar-items>
        </v-toolbar>

        <v-card
          v-if="downloadProfileHtml"
          flat
          color="grey"
          :min-height="previewProfileCardHeight"
          class="mt-6 pb-6"
        >
          <div
            style="display: flex; flex-direction: column; align-items: center"
          >
            <div v-html="downloadProfileHtml"></div>
          </div>
        </v-card>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import QrCodeHelper from "qrcode-svg";

import cardHtmlTemplate from "../assets/templates/card_template.htm";
import { printHtmlAsPdf } from "@/app/core-module/util/coreHelper";
import { bulkDownloadProfileCards } from "@/app/user-module/helpers/profileCardHelper";
import { bulkDownloadTrainingCertificates } from "@/app/user-module/helpers/certificateHelper";

export default {
  name: "ProfileQrCode",
  data() {
    return {
      shareFab: false,
      copyingShareUrl: false,
      loading: true,
      userProfile: null,
      subscriberInfo: null,
      downloadProfileDialog: false,
      downloadProfileLoading: false,
      downloadCertificatesLoading: false,
      downloadQrCodeLoading: false,
      downloadProfileHtml: null,
      downloadProfileCardHeight: 600,
      profileCardAspectRatio: 1.75,
    };
  },
  computed: {
    shareUrl() {
      const domainUrl = `${process.env.VUE_APP_EMPLOYEE_URL}/${
        this.$i18n.locale || "en"
      }`;

      return `${domainUrl}/public-profile/${this.$store.state.authenticatedUser?._id}`;
    },
    shareTargets() {
      return ["linkedin", "twitter", "facebook", "whatsapp", "email"];
    },
    previewProfileCardHeight() {
      return Math.min(
        300,
        (this.$vuetify.breakpoint.width * 0.75) / this.profileCardAspectRatio
      );
    },
    isPublicProfile() {
      return this.$route.name === "PublicProfile";
    },
    getProfileVariables() {
      return {
        id: this.isPublicProfile ? this.$route.params.id : undefined,
      };
    },
  },
  apollo: {
    getProfile() {
      return {
        query: require("../api/query/getProfile.gql"),
        variables: this.getProfileVariables,
        result({ data, loading }) {
          if (data && data.getProfile) {
            this.userProfile = data.getProfile;
          }

          this.loading = loading;
        },
      };
    },
    getSubscriberProfile() {
      return {
        query: require("../api/query/getSubscriberProfile.gql"),
        result({ data }) {
          if (data?.getSubscriberProfile) {
            this.subscriberInfo = data.getSubscriberProfile;
          }
        },
      };
    },
  },
  watch: {
    isPublicProfile() {
      this.refetchGetProfile();
    },
  },
  methods: {
    refetchGetProfile() {
      this.loading = true;
      this.userProfile = null;
      this.$apollo.queries.getProfile.refetch(this.getProfileVariables);
    },
    qrCodeSvg(size, isSvgViewBox = false) {
      return new QrCodeHelper({
        content: this.shareUrl,
        padding: 0,
        width: size,
        height: size,
        color: "#281166",
        container: isSvgViewBox ? "svg-viewbox" : "svg",
      }).svg();
    },
    async copyToClipBoard(text) {
      this.copyingShareUrl = true;

      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      } else {
        const tmpTextField = document.createElement("textarea");
        tmpTextField.textContent = text;
        tmpTextField.setAttribute("style", "position: absolute; right: 200%;");
        document.body.appendChild(tmpTextField);
        tmpTextField.select();
        tmpTextField.setSelectionRange(0, 99999); /*For mobile devices*/
        document.execCommand("copy");
        tmpTextField.remove();
      }

      this.copyingShareUrl = false;
    },
    async downloadQrCode() {
      if (this.downloadQrCodeLoading) return;

      this.downloadQrCodeLoading = true;

      await this.downloadHtmlAsImage({
        dataList: [
          {
            htmlElement: this.$refs.qrCodeSvg,
            fileName: `my-qr-code-${Date.now()}`,
          },
        ],
      });

      this.downloadQrCodeLoading = false;
    },
    getProfileCardHtmlTemplate() {
      let htmlTemplate = cardHtmlTemplate;

      //employeeAvatarHidden
      //employeeAvatar
      //
      //employeeNameHidden
      //employeeNameTitle
      //employeeName
      //
      //employeeDesignationHidden
      //employeeDesignationTitle
      //employeeDesignationShort
      //employeeDesignationLong
      //
      //employeeCompanyNameHidden
      //employeeCompanyNameTitle
      //employeeCompanyName
      //
      //employeeNoHidden
      //employeeNoTitle
      //employeeNo
      //
      //employeeCompanyLogoHidden
      //employeeCompanyLogo
      //
      //course1Hidden
      //course1Title
      //course1ExpireHidden
      //course1Expire
      //
      //course2Hidden
      //course2Title
      //course2ExpireHidden
      //course2Expire
      //
      //course3Hidden
      //course3Title
      //course3ExpireHidden
      //course3Expire
      //
      //employeeQrCodeHidden
      //employeeQrCode
      //scanMe

      const profile = this.userProfile;
      const roleKey = profile?.role?.toLowerCase();

      //region avatar
      if (profile?.avatar) {
        htmlTemplate = htmlTemplate.replace(/{{employeeAvatarHidden}}/g, "");

        htmlTemplate = htmlTemplate.replace(
          /{{employeeAvatar}}/g,
          this.getImage(profile?.avatar)
        );
      } else {
        htmlTemplate = htmlTemplate.replace(
          /{{employeeAvatarHidden}}/g,
          "hidden"
        );

        htmlTemplate = htmlTemplate.replace(
          /{{employeeAvatar}}/g,
          require("@/app/core-module/assets/images/LOGO.png")
        );
      }
      //endregion

      //region name
      let employeeName = profile?.firstName?.trim() ?? "";
      const lastName = profile?.lastName?.trim() ?? "";
      if (lastName?.length) employeeName += ` ${lastName} `;

      if (employeeName?.length) {
        htmlTemplate = htmlTemplate.replace(/{{employeeNameHidden}}/g, "");

        htmlTemplate = htmlTemplate.replace(
          /{{employeeNameTitle}}/g,
          this.$t("user.profile.name")
        );

        htmlTemplate = htmlTemplate.replace(/{{employeeName}}/g, employeeName);
      } else {
        htmlTemplate = htmlTemplate.replace(
          /{{employeeNameHidden}}/g,
          "hidden"
        );

        htmlTemplate = htmlTemplate.replace(/{{employeeNameTitle}}/g, "");

        htmlTemplate = htmlTemplate.replace(/{{employeeName}}/g, "");
      }
      //endregion

      //region designation
      if (profile[roleKey]?.designation) {
        htmlTemplate = htmlTemplate.replace(
          /{{employeeDesignationHidden}}/g,
          ""
        );

        htmlTemplate = htmlTemplate.replace(
          /{{employeeDesignationTitle}}/g,
          this.$t("user.profile.designation")
        );

        htmlTemplate = htmlTemplate.replace(
          /{{employeeDesignationShort}}/g,
          profile[roleKey]?.designation
        );

        htmlTemplate = htmlTemplate.replace(
          /{{employeeDesignationLong}}/g,
          profile[roleKey]?.designation
        );
      } else {
        htmlTemplate = htmlTemplate.replace(
          /{{employeeDesignationHidden}}/g,
          "hidden"
        );

        htmlTemplate = htmlTemplate.replace(
          /{{employeeDesignationTitle}}/g,
          ""
        );

        htmlTemplate = htmlTemplate.replace(
          /{{employeeDesignationShort}}/g,
          ""
        );

        htmlTemplate = htmlTemplate.replace(/{{employeeDesignationLong}}/g, "");
      }
      //endregion

      //region company name
      if (profile[roleKey]?.organization?.name) {
        htmlTemplate = htmlTemplate.replace(
          /{{employeeCompanyNameHidden}}/g,
          ""
        );

        htmlTemplate = htmlTemplate.replace(
          /{{employeeCompanyNameTitle}}/g,
          this.$t("user.profile.companyName")
        );

        htmlTemplate = htmlTemplate.replace(
          /{{employeeCompanyName}}/g,
          this.getLocalisedValue(profile[roleKey]?.organization?.name)
        );
      } else {
        htmlTemplate = htmlTemplate.replace(
          /{{employeeCompanyNameHidden}}/g,
          "hidden"
        );

        htmlTemplate = htmlTemplate.replace(
          /{{employeeCompanyNameTitle}}/g,
          ""
        );

        htmlTemplate = htmlTemplate.replace(/{{employeeCompanyName}}/g, "");
      }
      //endregion

      //region employee no
      if (profile[roleKey]?.employeeNo) {
        htmlTemplate = htmlTemplate.replace(/{{employeeNoHidden}}/g, "");

        htmlTemplate = htmlTemplate.replace(
          /{{employeeNoTitle}}/g,
          this.$t("user.profile.employeeNo")
        );

        htmlTemplate = htmlTemplate.replace(
          /{{employeeNo}}/g,
          profile[roleKey]?.employeeNo
        );
      } else {
        htmlTemplate = htmlTemplate.replace(/{{employeeNoHidden}}/g, "hidden");

        htmlTemplate = htmlTemplate.replace(/{{employeeNoTitle}}/g, "");

        htmlTemplate = htmlTemplate.replace(/{{employeeNo}}/g, "");
      }
      //endregion

      //region company logo
      if (profile[roleKey]?.organization?.logo) {
        htmlTemplate = htmlTemplate.replace(
          /{{employeeCompanyLogoHidden}}/g,
          ""
        );

        htmlTemplate = htmlTemplate.replace(
          /{{employeeCompanyLogo}}/g,
          this.getImage(profile[roleKey]?.organization?.logo)
        );
      } else {
        htmlTemplate = htmlTemplate.replace(
          /{{employeeCompanyLogoHidden}}/g,
          "hidden"
        );

        htmlTemplate = htmlTemplate.replace(
          /{{employeeCompanyLogo}}/g,
          require("@/app/core-module/assets/images/LOGO.png")
        );
      }
      //endregion

      const course1Certificate =
        profile[roleKey]?.trainingCertificates?.length >= 1
          ? profile[roleKey]?.trainingCertificates[0]
          : null;
      const course2Certificate =
        profile[roleKey]?.trainingCertificates?.length >= 2
          ? profile[roleKey]?.trainingCertificates[1]
          : null;
      const course3Certificate =
        profile[roleKey]?.trainingCertificates?.length >= 3
          ? profile[roleKey]?.trainingCertificates[2]
          : null;

      //region course1
      if (course1Certificate?.trainingTitle) {
        htmlTemplate = htmlTemplate.replace(/{{course1Hidden}}/g, "");

        htmlTemplate = htmlTemplate.replace(
          /{{course1Title}}/g,
          this.getLocalisedValue(course1Certificate.trainingTitle)
        );

        if (course1Certificate?.expiresAt) {
          htmlTemplate = htmlTemplate.replace(/{{course1ExpireHidden}}/g, "");

          htmlTemplate = htmlTemplate.replace(
            /{{course1Expire}}/g,
            `${this.$t("user.profile.expiresOn")} ${this.$moment(
              parseInt(course1Certificate.expiresAt)
            ).format("DD MMMM YYYY")}`
          );
        } else {
          htmlTemplate = htmlTemplate.replace(
            /{{course1ExpireHidden}}/g,
            "hidden"
          );

          htmlTemplate = htmlTemplate.replace(/{{course1Expire}}/g, "");
        }
      } else {
        htmlTemplate = htmlTemplate.replace(/{{course1Hidden}}/g, "hidden");

        htmlTemplate = htmlTemplate.replace(/{{course1Title}}/g, "");

        htmlTemplate = htmlTemplate.replace(
          /{{course1ExpireHidden}}/g,
          "hidden"
        );

        htmlTemplate = htmlTemplate.replace(/{{course1Expire}}/g, "");
      }
      //endregion

      //region course2
      if (course2Certificate?.trainingTitle) {
        htmlTemplate = htmlTemplate.replace(/{{course2Hidden}}/g, "");

        htmlTemplate = htmlTemplate.replace(
          /{{course2Title}}/g,
          this.getLocalisedValue(course2Certificate.trainingTitle)
        );

        if (course2Certificate?.expiresAt) {
          htmlTemplate = htmlTemplate.replace(/{{course2ExpireHidden}}/g, "");

          htmlTemplate = htmlTemplate.replace(
            /{{course2Expire}}/g,
            `${this.$t("user.profile.expiresOn")} ${this.$moment(
              parseInt(course2Certificate.expiresAt)
            ).format("DD MMMM YYYY")}`
          );
        } else {
          htmlTemplate = htmlTemplate.replace(
            /{{course2ExpireHidden}}/g,
            "hidden"
          );

          htmlTemplate = htmlTemplate.replace(/{{course2Expire}}/g, "");
        }
      } else {
        htmlTemplate = htmlTemplate.replace(/{{course2Hidden}}/g, "hidden");

        htmlTemplate = htmlTemplate.replace(/{{course2Title}}/g, "");

        htmlTemplate = htmlTemplate.replace(
          /{{course2ExpireHidden}}/g,
          "hidden"
        );

        htmlTemplate = htmlTemplate.replace(/{{course2Expire}}/g, "");
      }
      //endregion

      //region course3
      if (course3Certificate?.trainingTitle) {
        htmlTemplate = htmlTemplate.replace(/{{course3Hidden}}/g, "");

        htmlTemplate = htmlTemplate.replace(
          /{{course3Title}}/g,
          this.getLocalisedValue(course3Certificate.trainingTitle)
        );

        if (course3Certificate?.expiresAt) {
          htmlTemplate = htmlTemplate.replace(/{{course3ExpireHidden}}/g, "");

          htmlTemplate = htmlTemplate.replace(
            /{{course3Expire}}/g,
            `${this.$t("user.profile.expiresOn")} ${this.$moment(
              parseInt(course3Certificate.expiresAt)
            ).format("DD MMMM YYYY")}`
          );
        } else {
          htmlTemplate = htmlTemplate.replace(
            /{{course3ExpireHidden}}/g,
            "hidden"
          );

          htmlTemplate = htmlTemplate.replace(/{{course3Expire}}/g, "");
        }
      } else {
        htmlTemplate = htmlTemplate.replace(/{{course3Hidden}}/g, "hidden");

        htmlTemplate = htmlTemplate.replace(/{{course3Title}}/g, "");

        htmlTemplate = htmlTemplate.replace(
          /{{course3ExpireHidden}}/g,
          "hidden"
        );

        htmlTemplate = htmlTemplate.replace(/{{course3Expire}}/g, "");
      }
      //endregion

      //region qrcode
      const qrCodeSvg = this.qrCodeSvg(this.previewProfileCardHeight / 2);

      if (qrCodeSvg) {
        htmlTemplate = htmlTemplate.replace(/{{employeeQrCodeHidden}}/g, "");

        htmlTemplate = htmlTemplate.replace(/{{employeeQrCode}}/g, qrCodeSvg);

        htmlTemplate = htmlTemplate.replace(
          /{{scanMe}}/g,
          this.$t("user.profile.scanMe")
        );
      } else {
        htmlTemplate = htmlTemplate.replace(
          /{{employeeQrCodeHidden}}/g,
          "hidden"
        );

        htmlTemplate = htmlTemplate.replace(/{{employeeQrCode}}/g, "");

        htmlTemplate = htmlTemplate.replace(/{{scanMe}}/g, "");
      }
      //endregion

      //region card size
      htmlTemplate = htmlTemplate.replace(
        /height="600"/g,
        `height="${this.previewProfileCardHeight}"`
      );

      htmlTemplate = htmlTemplate.replace(
        /calc\(600px/g,
        `calc(${this.previewProfileCardHeight}px`
      );
      //endregion

      return htmlTemplate;
    },
    previewProfileCard() {
      if (this.downloadProfileDialog) return;

      this.downloadProfileLoading = true;
      this.downloadProfileHtml = this.getProfileCardHtmlTemplate();
      this.downloadProfileLoading = false;
      this.downloadProfileDialog = true;

      // for testing
      // this.downloadHtmlAsImage({
      //   htmlTemplate,
      //   download: true,
      //   dataList: [
      //     {
      //       elementId: "card-front-side",
      //       fileName: `my-card-front-${Date.now()}`,
      //     },
      //     {
      //       elementId: "card-back-side",
      //       fileName: `my-card-back-${Date.now()}`,
      //     },
      //   ],
      // });
    },
    async downloadProfileCardAsPdf() {
      if (this.downloadProfileLoading) return;
      this.downloadProfileLoading = true;

      // for testing
      // await this.printHtmlAsPdf({
      //   htmlTemplate: `
      //       <div style="display: flex;flex-direction: column; align-items: center;">
      //           ${document.getElementById("profile-card-front-back").innerHTML}
      //       </div>
      //   `,
      // });

      await bulkDownloadProfileCards({
        employeeProfiles: [
          {
            ...this.userProfile.employee,
            user: this.userProfile,
          },
        ],
        subscriberInfo: this.subscriberInfo,
        image: true,
      });

      // await this.downloadHtmlAsPdf({
      //   imageWidth:
      //     this.downloadProfileCardHeight * this.profileCardAspectRatio,
      //   imageHeight: this.downloadProfileCardHeight * 2 + 20,
      //   imageScaling: 6,
      //   data: {
      //     htmlElement: document.getElementById("profile-card-front-back"),
      //     elementId: "profile-card-front-back",
      //     fileName: `my-card-${Date.now()}`,
      //   },
      // });

      this.downloadProfileLoading = false;
    },
    async downloadProfileCardAsImage() {
      if (this.downloadProfileLoading) return;
      this.downloadProfileLoading = true;

      await this.downloadHtmlAsImage({
        imageWidth:
          this.downloadProfileCardHeight * this.profileCardAspectRatio,
        imageHeight: this.downloadProfileCardHeight * 2 + 20,
        imageScaling: 2,
        dataList: [
          {
            htmlElement: document.getElementById("profile-card-front-back"),
            elementId: "profile-card-front-back",
            fileName: `my-card-${Date.now()}`,
          },
        ],
      });

      this.downloadProfileLoading = false;
    },
    async downloadCertificatesAsPdf() {
      if (this.downloadCertificatesLoading) return;
      this.downloadCertificatesLoading = true;

      await bulkDownloadTrainingCertificates({
        trainingCertificates:
          this.userProfile?.employee?.trainingCertificates?.map(x => ({
            ...x,
            //to get employee user avatar
            employee: {
              user: {
                ...this.userProfile,
              },
            },
          })) ?? [],
        subscriberInfo: this.subscriberInfo,
      });

      this.downloadCertificatesLoading = false;
    },
  },
};
</script>
